// src/components/invoices/InvoiceDetails.tsx
import {
  Alert,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Menu,
  Portal,
  Separator,
  Stat,
  Text,
  VStack,
  useMediaQuery,
} from '@chakra-ui/react'
import {
  LuArchive,
  LuEye,
  LuFileCode2,
  LuFileDown,
  LuFileText,
  LuMail,
  LuRefreshCcw,
  LuX,
  LuTrash2,
  LuPencil,
} from 'react-icons/lu'
import type { Invoice, InvoiceLine } from './invoice.types'
import {
  canDownload,
  formatBillingVariant,
  formatDateDE,
  formatEuro,
  getLocale,
  localizeCancellationComment,
} from '../../helper/invoice.helpers'
import { useMemo, useState } from 'react'
import React from 'react'
import { SendInvoiceConfirmDialog } from './SendInvoiceConfirmDialog'

function InvoiceMessage({
  variant,
  title,
  body,
  action,
}: {
  variant: 'success' | 'danger' | 'info'
  title: string
  body: string
  action?: React.ReactNode
}) {
  const status = variant === 'success' ? 'success' : variant === 'info' ? 'warning' : 'error'

  return (
    <Alert.Root status={status}>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{title}</Alert.Title>
        <Alert.Description>{body}</Alert.Description>
      </Alert.Content>
      <Flex direction={{ base: 'column', md: 'row' }} justify="flex-end">
        {action ? (
          <Box w={{ base: 'full', md: 'auto' }} display="flex" justifyContent="flex-end">
            {action}
          </Box>
        ) : null}
      </Flex>
    </Alert.Root>
  )
}

export default function InvoiceDetails({
  invoice,
  // unionEntities,
  onDownload,
  onPreview,
  onEditDraft,
  onDeleteDraft,
  onFinalizeDraft,
  onSubmitForApproval,
  onSendFinalized,
  onCancel,
  isSending,
  onDownloadCancellation,
  onOpenInvoice,
  onRegenerate,
}: {
  invoice: Invoice
  unionEntities: { id: number; name: string }[]
  onDownload?: (type: 'pdf' | 'xml' | 'zip') => void
  onWithdraw?: () => void
  onEditDraft?: () => void
  onDeleteDraft?: () => void
  onPreview?: () => void
  onFinalizeDraft?: () => void
  onSubmitForApproval?: () => void | Promise<void>
  onSendFinalized?: () => void
  onCancel?: () => void
  isSending?: boolean
  onDownloadCancellation?: (type: 'pdf' | 'xml' | 'zip') => void
  onOpenInvoice?: (id: string) => void
  onRegenerate?: () => void
}) {
  // const isOrderInvoice = invoice.scope === 'ORDER'
  const isUnionActivityInvoice = invoice.scope === 'ACTIVITY' && invoice.bankId === 1
  const cancellationReasonText = invoice.cancellationReason?.trim() ?? ''
  const [isSendConfirmOpen, setIsSendConfirmOpen] = useState(false)
  const isUnionFlow = invoice.bankId === 1
  const isPendingApproval = invoice.approvalStatus === 'PENDING_APPROVAL'
  const isRejected = invoice.approvalStatus === 'REJECTED'
  const isApproved = invoice.approvalStatus === 'APPROVED'
  const groupedSummary = useMemo(() => {
    const groups = new Map<string, InvoiceLine[]>()
    invoice.lines.forEach(l => {
      const key = l.serviceGroup
      groups.set(key, [...(groups.get(key) ?? []), l])
    })
    return Array.from(groups.entries()).map(([group, lines]) => {
      const subtotalCents = lines.reduce((acc, l) => acc + l.menge * l.preisProEinheitCents, 0)
      return { group, subtotalCents, lines }
    })
  }, [invoice])

  const locale = useMemo(() => getLocale(), [])
  const commentText = useMemo(
    () => localizeCancellationComment(invoice.comment),
    [invoice.comment, locale]
  )

  const financialSummary = useMemo(() => {
    const items = invoice.items ?? []

    const sumNet = items.reduce((acc: number, item) => acc + Number(item.netAmount ?? 0), 0)
    const sumTax = items.reduce((acc: number, item) => acc + Number(item.taxAmount ?? 0), 0)
    const sumGross = items.reduce((acc: number, item) => acc + Number(item.grossAmount ?? 0), 0)

    const taxRate = sumNet > 0 ? Math.round((sumTax / sumNet) * 100) : 19

    if (isUnionActivityInvoice) {
      const betweenNet = sumNet / 0.9

      return {
        betweenNetCents: Math.round(betweenNet * 100),
        factorPercent: 90,
        resultNetCents: Math.round(sumNet * 100),
        taxCents: Math.round(sumTax * 100),
        grossCents: Math.round(sumGross * 100),
        taxRatePercent: taxRate,
      }
    }

    return {
      betweenNetCents: Math.round(sumNet * 100),
      factorPercent: null,
      resultNetCents: Math.round(sumNet * 100),
      taxCents: Math.round(sumTax * 100),
      grossCents: Math.round(sumGross * 100),
      taxRatePercent: taxRate,
    }
  }, [invoice.items, isUnionActivityInvoice])

  const [isMobile] = useMediaQuery(['(max-width: 767px)'])

  const sentAt = invoice.sentAt ?? null
  const canceledAt = invoice.canceledAt ?? null

  const sentMsg = sentAt
    ? `Rechnung ${invoice.invoiceNumber} am ${formatDateDE(sentAt)} versendet.`
    : null

  const canceledMsg = canceledAt
    ? `Rechnung ${invoice.invoiceNumber} wurde am ${formatDateDE(canceledAt)} storniert.`
    : null

  const hasCancellationDoc = invoice.status === 'CANCELED' && !!invoice.cancellationInvoiceId

  const isActivity = (invoice.scope ?? invoice.type ?? '').toString().toUpperCase() === 'ACTIVITY'
  const showRegenerate = isActivity && invoice.status === 'DRAFT'

  const isCancellationInvoice = !!invoice.canceledInvoiceId
  // Union flow: a sent invoice may still be cancelled while Union Investment has not
  // reviewed it yet. Once approved or rejected, only downloads remain.
  const showCancelButton =
    invoice.status === 'SENT' &&
    isActivity &&
    !isCancellationInvoice &&
    (!isUnionActivityInvoice || isPendingApproval)
  function SummaryRow({
    label,
    value,
    align = 'left',
  }: {
    label: string
    value: React.ReactNode
    align?: 'left' | 'right'
  }) {
    return (
      <Stat.Root textAlign={align}>
        <Stat.Label>{label}</Stat.Label>
        <Stat.ValueText>{value}</Stat.ValueText>
      </Stat.Root>
    )
  }

  const showBillingVariantColumn = isActivity
  const actions = (
    <Flex
      gap={2}
      flexWrap="wrap"
      justify="flex-end"
      w="fit-content"
      mx="auto"
      alignSelf="center"
      justifyContent="center"
    >
      {invoice.status === 'DRAFT' ? (
        <>
          {isUnionFlow && isPendingApproval ? (
            <>
              <Button
                variant="subtle"
                colorPalette="blue"
                onClick={onPreview}
                disabled={!onPreview}
              >
                <LuEye /> Vorschau
              </Button>
              <Button
                variant="subtle"
                colorPalette="red"
                onClick={onDeleteDraft}
                disabled={!onDeleteDraft}
              >
                Prozess abbrechen & Vorschau löschen
              </Button>
            </>
          ) : isUnionFlow && isRejected ? (
            <>
              <Button
                variant="subtle"
                colorPalette="blue"
                onClick={onPreview}
                disabled={!onPreview}
              >
                <LuEye /> Vorschau
              </Button>
              <Button
                variant="subtle"
                colorPalette="red"
                onClick={onDeleteDraft}
                disabled={!onDeleteDraft}
              >
                Vorschau löschen
              </Button>
            </>
          ) : isUnionFlow ? (
            <>
              <Button
                variant="solid"
                colorPalette="blue"
                onClick={onSubmitForApproval}
                disabled={!onSubmitForApproval || !!isSending}
                loading={!!isSending}
                data-testid="invoice-action-submit-approval"
              >
                Rechnung prüfen lassen
              </Button>

              <Button
                variant="subtle"
                colorPalette="blue"
                onClick={onPreview}
                disabled={!onPreview}
                data-testid="invoice-action-preview"
              >
                <LuEye /> Vorschau
              </Button>

              <Button
                variant="outline"
                colorPalette="blue"
                onClick={onEditDraft}
                disabled={!onEditDraft}
                data-testid="invoice-action-edit-preview"
              >
                <LuPencil /> Vorschau bearbeiten
              </Button>

              <Button
                variant="subtle"
                colorPalette="red"
                onClick={onDeleteDraft}
                disabled={!onDeleteDraft}
                data-testid="invoice-action-delete-draft"
              >
                <LuTrash2 />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="solid"
                colorPalette="blue"
                onClick={onFinalizeDraft}
                disabled={!onFinalizeDraft || !!isSending}
                loading={!!isSending || !onFinalizeDraft}
                data-testid="invoice-action-finalize"
              >
                Rechnung erstellen
              </Button>

              <Button
                variant="subtle"
                colorPalette="blue"
                onClick={onPreview}
                disabled={!onPreview}
              >
                <LuEye /> Vorschau
              </Button>

              <Button
                variant="outline"
                colorPalette="blue"
                onClick={onEditDraft}
                disabled={!onEditDraft}
              >
                <LuPencil /> Vorschau bearbeiten
              </Button>

              <Button
                variant="subtle"
                colorPalette="red"
                onClick={onDeleteDraft}
                disabled={!onDeleteDraft}
              >
                <LuTrash2 />
              </Button>
            </>
          )}
        </>
      ) : (
        <>
          <Menu.Root positioning={{ placement: 'bottom-start', gutter: 8 }}>
            <Menu.Trigger asChild>
              <Button variant="subtle" colorPalette="blue" disabled={!canDownload(invoice.status)}>
                <LuFileDown />
                E-Rechnung
              </Button>
            </Menu.Trigger>

            <Portal>
              <Menu.Positioner>
                <Menu.Content minW="unset" width="fit-content">
                  <HStack gap="2">
                    <Menu.Item value="pdf" asChild>
                      <Button variant="ghost" onClick={() => onDownload?.('pdf')}>
                        <LuFileText />
                        <Text>PDF</Text>
                      </Button>
                    </Menu.Item>

                    <Menu.Item value="xml" asChild>
                      <Button variant="ghost" onClick={() => onDownload?.('xml')}>
                        <LuFileCode2 />
                        <Text>XML</Text>
                      </Button>
                    </Menu.Item>

                    <Menu.Item value="zip" asChild>
                      <Button variant="ghost" onClick={() => onDownload?.('zip')}>
                        <LuArchive />
                        <Text>ZIP</Text>
                      </Button>
                    </Menu.Item>
                  </HStack>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>

          {hasCancellationDoc && (
            <Menu.Root positioning={{ placement: 'bottom-start', gutter: 8 }}>
              <Menu.Trigger asChild>
                <Button variant="subtle" colorPalette="red">
                  <LuFileDown />
                  Storno-Rechnung
                </Button>
              </Menu.Trigger>

              <Portal>
                <Menu.Positioner>
                  <Menu.Content minW="unset" width="fit-content">
                    <HStack gap="2">
                      <Menu.Item value="pdf" asChild>
                        <Button variant="ghost" onClick={() => onDownloadCancellation?.('pdf')}>
                          <LuFileText />
                          <Text>PDF</Text>
                        </Button>
                      </Menu.Item>

                      <Menu.Item value="xml" asChild>
                        <Button variant="ghost" onClick={() => onDownloadCancellation?.('xml')}>
                          <LuFileCode2 />
                          <Text>XML</Text>
                        </Button>
                      </Menu.Item>

                      <Menu.Item value="zip" asChild>
                        <Button variant="ghost" onClick={() => onDownloadCancellation?.('zip')}>
                          <LuArchive />
                          <Text>ZIP</Text>
                        </Button>
                      </Menu.Item>
                    </HStack>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          )}

          {invoice.status === 'FINALIZED' && !isUnionFlow && (
            <>
              <Button
                variant="solid"
                colorPalette="blue"
                onClick={() => setIsSendConfirmOpen(true)}
                disabled={!onSendFinalized || !!isSending}
                loading={!!isSending}
                data-testid="invoice-action-send"
              >
                <LuMail />
                Rechnung versenden
              </Button>

              {showRegenerate && (
                <Button
                  variant="solid"
                  colorPalette="yellow"
                  onClick={onRegenerate}
                  disabled={!onRegenerate}
                >
                  <LuRefreshCcw />
                  Rechnung erneut erstellen
                </Button>
              )}
              {/* <Button
                        variant="subtle"
                        colorPalette="red"
                        onClick={onDeleteDraft}
                        disabled={!onDeleteDraft || !!isSending}
                      >
                        <LuTrash2 />

                      </Button> */}
            </>
          )}

          {showCancelButton && (
            <Button
              variant="subtle"
              colorPalette="red"
              onClick={onCancel}
              disabled={!onCancel}
              data-testid="invoice-action-cancel"
            >
              <LuX />
              {isUnionActivityInvoice
                ? 'Prozess abbrechen & Stornorechnung erstellen'
                : 'Rechnung stornieren'}
            </Button>
          )}
        </>
      )}
    </Flex>
  )
  return (
    <Flex direction="column" minH={0} position="relative">
      {/* Top messages */}
      <Flex direction="column" gap={2} mt={2} mb={2}>
        {invoice.status === 'CANCELED' && canceledMsg && (
          <InvoiceMessage
            variant="danger"
            title="Rechnung storniert"
            body={canceledMsg}
            action={
              invoice.cancellationInvoiceId ? (
                <Button
                  variant="surface"
                  colorPalette="red"
                  onClick={() =>
                    invoice.cancellationInvoiceId &&
                    onOpenInvoice?.(invoice.cancellationInvoiceId!)
                  }
                >
                  Storno-Rechnung öffnen
                </Button>
              ) : null
            }
          />
        )}
        {isUnionFlow && isPendingApproval && (
          <InvoiceMessage
            variant="info"
            title="Wartet auf Freigabe"
            body={`Rechnung ${invoice.invoiceNumber} wartet auf Freigabe durch Union Investment.`}
          />
        )}

        {isUnionFlow && isRejected && (
          <InvoiceMessage
            variant="danger"
            title="Rechnung abgelehnt"
            body={`Rechnung ${invoice.invoiceNumber} wurde nicht freigegeben. Grund: ${invoice.rejectionReason?.trim() || 'kein Grund angegeben'}.`}
          />
        )}

        {isUnionFlow && isApproved && invoice.status === 'SENT' && (
          <>
            <InvoiceMessage
              variant="success"
              title="Freigegeben"
              body={`Rechnung ${invoice.invoiceNumber} durch Union Investment freigegeben.`}
            />
          </>
        )}
        {(invoice.status === 'SENT' || invoice.status === 'CANCELED') && sentMsg && (
          <InvoiceMessage
            variant="success"
            title={invoice.canceledInvoiceId ? 'Storno-Rechnung versendet' : 'Rechnung versendet'}
            body={
              invoice.contactDetails?.contactName || invoice.contactDetails?.contactEmail
                ? `Rechnung ${invoice.invoiceNumber} am ${formatDateDE(sentAt!)} an ${invoice.contactDetails?.contactName ?? 'den Rechnungsempfänger'}${invoice.contactDetails?.contactEmail ? ` (${invoice.contactDetails.contactEmail})` : ''} versendet.`
                : sentMsg
            }
            action={
              invoice.canceledInvoiceId ? (
                <Button
                  variant="surface"
                  colorPalette="green"
                  onClick={() =>
                    invoice.canceledInvoiceId && onOpenInvoice?.(invoice.canceledInvoiceId!)
                  }
                >
                  Ursprungsrechnung öffnen
                </Button>
              ) : null
            }
          />
        )}
      </Flex>

      {/* Header card (reused InvoiceCard)
      <InvoiceCard invoice={invoice} unionEntities={unionEntities} variant="header" /> */}

      {/* Body */}
      <Box flex="1" minH={0} overflowY="auto">
        <Flex direction="column" pr={1}>
          {/* <Separator variant="solid" borderColor="border" borderWidth="1.5px" mt="2" /> */}
          <Text>Zusammenfassung</Text>

          <VStack align="stretch">
            {groupedSummary.map(group => (
              <Box key={group.group}>
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  justify="space-between"
                  align="start"
                  gap="6"
                >
                  <Box>
                    <Text>{group.group}</Text>
                    <Text>{formatEuro(group.subtotalCents)}</Text>
                  </Box>

                  <Grid
                    templateColumns={
                      showBillingVariantColumn && !isMobile ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'
                    }
                    rowGap="2"
                    columnGap="4"
                    color="fg"
                    alignItems="center"
                  >
                    <GridItem>
                      <Text color="fg.muted">Kunde</Text>
                    </GridItem>
                    {showBillingVariantColumn && !isMobile && (
                      <GridItem>
                        <Text color="fg.muted" textAlign="right" whiteSpace="nowrap">
                          Abrechnungsvariante
                        </Text>
                      </GridItem>
                    )}
                    <GridItem>
                      <Text color="fg.muted" textAlign="right" whiteSpace="nowrap">
                        Menge
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text color="fg.muted" textAlign="right" whiteSpace="nowrap">
                        Preis pro Einheit
                      </Text>
                    </GridItem>

                    {group.lines.map(line => (
                      <React.Fragment key={line.id}>
                        <GridItem>
                          <Text color={line.customerName ? 'fg' : 'fg.muted'}>
                            {line.customerName ?? '—'}
                          </Text>
                        </GridItem>

                        {showBillingVariantColumn && !isMobile && (
                          <GridItem>
                            <Text textAlign="right" whiteSpace="nowrap">
                              {formatBillingVariant(line.billingVariant)}
                            </Text>
                          </GridItem>
                        )}

                        <GridItem>
                          <Text textAlign="right" whiteSpace="nowrap">
                            {line.menge}
                          </Text>
                        </GridItem>
                        <GridItem>
                          <Text textAlign="right" whiteSpace="nowrap">
                            {formatEuro(line.preisProEinheitCents)}
                          </Text>
                        </GridItem>
                      </React.Fragment>
                    ))}
                  </Grid>
                </Flex>
                <Separator my="5" borderColor="border" />
              </Box>
            ))}
            <Box>
              <VStack align="stretch" gap="6">
                <SummaryRow
                  label="Zwischenbetrag (netto)"
                  value={formatEuro(financialSummary.betweenNetCents)}
                />

                {isUnionActivityInvoice && (
                  <>
                    <SummaryRow
                      label="Leistungsfaktor"
                      value={`${formatEuro(financialSummary.betweenNetCents)} × ${financialSummary.factorPercent} %`}
                    />

                    <SummaryRow
                      label="Leistungsbetrag (netto)"
                      value={formatEuro(financialSummary.resultNetCents)}
                    />
                  </>
                )}

                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="6">
                  <SummaryRow label="MwSt." value={formatEuro(financialSummary.taxCents)} />

                  <SummaryRow
                    label="MwSt.-Satz"
                    value={`${financialSummary.taxRatePercent} %`}
                    align={isMobile ? 'left' : 'right'}
                  />
                </Grid>

                <SummaryRow
                  label="Rechnungsbetrag (brutto)"
                  value={formatEuro(financialSummary.grossCents)}
                />
              </VStack>

              <Separator my="5" borderColor="border" />
            </Box>

            <Box mb="5">
              <Text>Notiz</Text>
              <Text>{commentText?.trim() ? commentText : '—'}</Text>
            </Box>
            {cancellationReasonText ? (
              <Box mb="5">
                <Text>Stornierungsgrund</Text>
                <Text>{cancellationReasonText}</Text>
              </Box>
            ) : null}
          </VStack>
        </Flex>
      </Box>
      {/* Sticky footer actions (BOTTOM) */}
      <Box position="sticky" bottom={0} zIndex={2}>
        <Flex justify="center">
          <Box w="fit-content" mx="auto">
            {actions}
          </Box>
        </Flex>
      </Box>
      <SendInvoiceConfirmDialog
        isOpen={isSendConfirmOpen}
        onClose={() => setIsSendConfirmOpen(false)}
        isSending={!!isSending}
        invoiceNumber={invoice.invoiceNumber}
        contactName={invoice.contactDetails?.contactName}
        contactEmail={invoice.contactDetails?.contactEmail}
        onConfirm={async () => {
          try {
            await onSendFinalized?.()
            setIsSendConfirmOpen(false)
          } catch {
            // parent handles error toast
          }
        }}
      />
    </Flex>
  )
}
